import { createContext, useCallback, useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import type { RealtimeHandler } from './api/eventStream';
import type { AppServices } from './services';
import type { SessionState } from './session/sessionStore';
import type { ClinicRole } from './session/roles';
import type { SessionMembership } from './session/types';

export const AppServicesContext = createContext<AppServices | null>(null);

export function useServices(): AppServices {
  const services = useContext(AppServicesContext);
  if (!services) throw new Error('useServices must be used inside AppProviders');
  return services;
}

/** Состояние сессии (подписка на SessionStore). */
export function useSessionState(): SessionState {
  const { session } = useServices();
  return useSyncExternalStore(session.subscribe, session.getState, session.getState);
}

export interface SessionView {
  state: SessionState;
  /** Членство в выбранной клинике. */
  membership: SessionMembership | null;
  /** Роли в выбранной клинике. */
  roles: ClinicRole[];
}

export function useSession(): SessionView {
  const state = useSessionState();
  const membership = state.user?.memberships.find((item) => item.clinicId === state.clinicId) ?? null;
  return { state, membership, roles: membership?.roles ?? [] };
}

/** Подписка компонента на событие реального времени (`scribe.note.ready`, `*`). */
export function useRealtimeEvent(type: string, handler: RealtimeHandler): void {
  const { events } = useServices();
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  const stableHandler = useCallback<RealtimeHandler>((message) => handlerRef.current(message), []);
  useEffect(() => events.subscribe(type, stableHandler), [events, type, stableHandler]);
}
